import { INCLUDED_SHADOW_INDEXES } from './constants';
import { generateShadows } from './generateShadows';

import type { Theme } from '@mui/material';

export const getDefaultTheme = (initTheme: Theme): Theme => ({
  ...initTheme,
  palette: {
    ...initTheme.palette,
    primary: {
      main: '#2763AA',
      dark: '#1E4A80',
      light: '#357AD0',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#9C27B0',
      dark: '#7B1FA2',
      light: '#BA68C8',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D32F2F',
      dark: '#C62828',
      light: '#EF5350',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#0288D1',
      dark: '#01579B',
      light: '#03A9F4',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#2E7D32',
      dark: '#1B5E20',
      light: '#4CAF50',
      contrastText: '#FFFFFF',
    },
    greys: {
      main: '#EEEEEE',
      dark: '#E0E0E0',
      light: '#FAFAFA',
      contrastText: '#000000DE',
      accent: '#BDBDBD',
    },
    indigo: {
      main: '#E8EAF6',
      dark: '#C5CAE9',
      light: '#F4F5FB',
      contrastText: '#1A237E',
      accent: '#5C6BC0',
    },
    amber: {
      main: '#FFF8E1',
      dark: '#FFECB3',
      light: '#FFFBF0',
      contrastText: '#FF6F00',
      accent: '#FFCA28',
    },
    orange: {
      main: '#FFF3E0',
      dark: '#FFE0B2',
      light: '#FFF9F0',
      contrastText: '#E65100',
      accent: '#FFA726',
    },
    deepOrange: {
      main: '#FBE9E7',
      dark: '#FFCCBC',
      light: '#FEF7F6',
      contrastText: '#BF360C',
      accent: '#FF7043',
    },
    green: {
      main: '#E8F5E9',
      dark: '#C8E6C9',
      light: '#F4FAF5',
      contrastText: '#1B5E20',
      accent: '#66BB6A',
    },
    lightGreen: {
      main: '#F1F8E9',
      dark: '#DCEDC8',
      light: '#F8FBF3',
      contrastText: '#33691E',
      accent: '#8BC34A',
    },
    blue: {
      main: '#E3F2FD',
      dark: '#BBDEFB',
      light: '#F1F8FE',
      contrastText: '#0D47A1',
      accent: '#42A5F5',
    },
    lightBlue: {
      main: '#E1F5FE',
      dark: '#B3E5FC',
      light: '#F0FAFF',
      contrastText: '#01579B',
      accent: '#29B6F6',
    },
    blueGrey: {
      main: '#ECEFF1',
      dark: '#CFD8DC',
      light: '#F6F8F9',
      contrastText: '#263238',
      accent: '#78909C',
    },
    yellow: {
      main: '#FFFDE7',
      dark: '#FFF9C4',
      light: '#FFFEF5',
      contrastText: '#F57F17',
      accent: '#FFEE58',
    },
    cyan: {
      main: '#E0F7FA',
      dark: '#B2EBF2',
      light: '#EDFAFC',
      contrastText: '#006064',
      accent: '#26C6DA',
    },
    red: {
      main: '#FEEBEE',
      dark: '#FECDD2',
      light: '#FFFAFB',
      contrastText: '#B71C1C',
      accent: '#EF5350',
    },
    teal: {
      main: '#E0F2F1',
      dark: '#B2DFDB',
      light: '#E9F6F5',
      contrastText: '#004D40',
      accent: '#26A69A',
    },
    pink: {
      main: '#FCE4EC',
      dark: '#F8BBD0',
      light: '#FDF1F5',
      contrastText: '#880E4F',
      accent: '#EC407A',
    },
    purple: {
      main: '#F3E5F5',
      dark: '#E1BEE7',
      light: '#F8F0F9',
      contrastText: '#4A148C',
      accent: '#AB47BC',
    },
    deepPurple: {
      main: '#EDE7F6',
      dark: '#D1C4E9',
      light: '#F7F4FB',
      contrastText: '#311B92',
      accent: '#7E57C2',
    },
    lime: {
      main: '#F9FBE7',
      dark: '#F0F4C3',
      light: '#FDFEF6',
      contrastText: '#827717',
      accent: '#D4E157',
    },
    background: {
      paper: '#F9FAFB',
      default: '#F9FAFB',
      secondary: '#4183D34D',
    },
  },
  components: {
    ...initTheme.components,
    MuiTypography: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          background: theme.palette.background.paper,
          borderRadius: '16px',
          marginTop: theme.spacing(1.5),
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '& tr:last-of-type td': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        thumb: {
          boxShadow: initTheme.shadows[1],
        },
      },
    },
  },
  shadows: generateShadows(initTheme.shadows, INCLUDED_SHADOW_INDEXES),
});
